import { AirlineLogo } from '../ui/AirlineLogo';
import { RouteDisplay } from '../ui/RouteDisplay';

const BARCODE_BARS = Array.from({ length: 40 }, () => ({
  width: Math.random() > 0.5 ? '2px' : '1px',
  height: `${12 + Math.random() * 8}px`,
}));

interface BoardingPassPreviewProps {
  booking: {
    pnr: string;
    airline_code: string;
    booking_date?: string;
    total_price_paid: number;
    currency: string;
    is_award_booking: boolean;
    cabin_class?: string;
    segments: Array<{
      carrier_code: string;
      flight_number: string;
      origin: string;
      destination: string;
      departure_datetime: string;
      arrival_datetime?: string;
    }>;
  };
}

/**
 * Boarding pass-styled preview card shown after booking import.
 * Uses a dark theme with perforated edges and a simulated barcode
 * to resemble a physical boarding pass. Multi-leg itineraries show
 * the full route (first origin -> last destination) in the hero
 * section, with individual segments listed below.
 */
export function BoardingPassPreview({ booking }: BoardingPassPreviewProps) {
  const firstSegment = booking.segments[0];
  const lastSegment = booking.segments[booking.segments.length - 1];

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="boarding-pass-enter">
      {/* Main boarding pass card */}
      <div className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 rounded-2xl overflow-hidden shadow-xl">
        {/* Perforated edge effect */}
        <div className="absolute left-0 top-0 bottom-0 w-4 flex flex-col justify-around py-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="w-2 h-2 bg-stone-100 rounded-full -ml-1" />
          ))}
        </div>

        {/* Header */}
        <div className="pl-6 pr-4 pt-4 pb-3 border-b border-stone-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AirlineLogo code={booking.airline_code} size={36} />
              <div>
                <p className="text-white font-bold text-lg tracking-wide">BOARDING PASS</p>
                <p className="text-stone-400 text-xs">Electronic Ticket</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-stone-400 text-xs">CONFIRMATION</p>
              <p className="text-white font-mono font-bold text-xl tracking-widest">{booking.pnr}</p>
            </div>
          </div>
        </div>

        {/* Route section */}
        <div className="pl-6 pr-4 py-5">
          <div className="flex items-center justify-between">
            {/* Origin */}
            <div className="text-center">
              <p className="text-white font-mono text-4xl font-bold tracking-wider">
                {firstSegment?.origin || '---'}
              </p>
              <p className="text-stone-400 text-xs mt-1">DEPART</p>
            </div>

            {/* Flight path visualization */}
            <div className="flex-1 px-6">
              <div className="relative flex items-center justify-center">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-stone-600 to-transparent" />
                <div className="absolute bg-stone-800 px-2">
                  <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                  </svg>
                </div>
              </div>
              <p className="text-center text-stone-500 text-xs mt-2">
                {booking.segments.length > 1 ? `${booking.segments.length} flights` : 'Direct'}
              </p>
            </div>

            {/* Destination */}
            <div className="text-center">
              <p className="text-white font-mono text-4xl font-bold tracking-wider">
                {lastSegment?.destination || '---'}
              </p>
              <p className="text-stone-400 text-xs mt-1">ARRIVE</p>
            </div>
          </div>
        </div>

        {/* Perforation divider — boarding pass tear line between route and details */}
        <div className="perforation-divider mx-6 my-1" />

        {/* Flight details */}
        <div className="pl-6 pr-4 pb-4 grid grid-cols-3 gap-4">
          <div>
            <p className="text-stone-500 text-xs mb-1">DATE</p>
            <p className="text-white font-medium text-sm">
              {firstSegment ? formatDate(firstSegment.departure_datetime) : '---'}
            </p>
          </div>
          <div>
            <p className="text-stone-500 text-xs mb-1">TIME</p>
            <p className="text-white font-medium text-sm">
              {firstSegment ? formatTime(firstSegment.departure_datetime) : '---'}
            </p>
          </div>
          <div>
            <p className="text-stone-500 text-xs mb-1">FLIGHT</p>
            <p className="text-white font-medium text-sm">
              {firstSegment ? `${firstSegment.carrier_code}${firstSegment.flight_number}` : '---'}
            </p>
          </div>
        </div>

        {/* Perforation divider — tear line before price stub */}
        <div className="perforation-divider mx-6 my-1" />

        {/* Price section */}
        <div className="pl-6 pr-4 py-4 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-stone-400 text-xs">PRICE PAID</p>
              <p className="text-white font-bold text-2xl font-data tabular-nums">
                {booking.is_award_booking
                  ? `${booking.total_price_paid.toLocaleString()} pts`
                  : `${booking.currency} ${booking.total_price_paid.toLocaleString()}`
                }
              </p>
            </div>
            {booking.is_award_booking && (
              <span className="px-3 py-1.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-bold rounded-full shadow-lg">
                AWARD
              </span>
            )}
          </div>
        </div>

        {/* Bottom barcode simulation */}
        <div className="pl-6 pr-4 py-3 bg-stone-950/50">
          <div className="flex items-center gap-1">
            {BARCODE_BARS.map((bar, i) => (
              <div
                key={i}
                className="bg-stone-600"
                style={bar}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Segments detail (if multi-leg) */}
      {booking.segments.length > 1 && (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-medium text-stone-500 dark:text-stone-400 uppercase">All Segments</p>
          {booking.segments.map((seg, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 bg-stone-100 dark:bg-stone-800 rounded-lg border border-stone-200 dark:border-stone-700"
            >
              <div className="w-8 h-8 bg-white dark:bg-stone-900 rounded-full flex items-center justify-center text-sm font-bold text-stone-600 dark:text-stone-400">
                {i + 1}
              </div>
              <div className="flex-1">
                <p className="font-medium text-stone-900 dark:text-stone-100">
                  {seg.carrier_code}{seg.flight_number}
                </p>
                <RouteDisplay
                  segments={[{ origin: seg.origin, destination: seg.destination }]}
                  size="sm"
                />
              </div>
              <div className="text-right text-sm text-stone-500 dark:text-stone-400">
                {formatDate(seg.departure_datetime)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
